/**
 * 🔴 **THE TWO HALVES OF A SELF-SERVICE RESET** (D122, PNK-143).
 *
 * Devin, looking at the sign-in screen: *"there's nothing that offers me the ability
 * to reset my password."*
 *
 * Deliberately NOT `authFetch`, for the same reason `signIn.ts` is not: there is no
 * session, and these are the requests somebody makes precisely because they cannot
 * get one.
 *
 * 🔴 **`requestReset` RESOLVES THE SAME WAY WHATEVER HAPPENED** — it has no failure
 * the caller is allowed to see, and that is a deliberate property of the endpoint
 * rather than laziness here. The server answers `{ ok: true }` for an address it has
 * never seen, for a deactivated account and for a caller over the rate limit, because
 * anything else turns the endpoint into an account-enumeration oracle: on this
 * platform the addresses belong to performers and crew, so confirming one confirms
 * that a named person is on this agency's books. A screen that showed "no such
 * account" would re-open from the client exactly what the server closed.
 *
 * So the only thing this function reports is whether the REQUEST itself got through.
 * A dead network is worth telling somebody about; the server's answer is not, because
 * it is the same answer every time.
 */
import { API_BASE_URL } from "@/api/base";
import type { FetchLike } from "./http";

export const RESET_REQUEST_PATH = "/auth/reset/request";
export const RESET_REDEEM_PATH = "/auth/reset/redeem";

/** Generous: this call sends mail, and a slow relay must not read as a broken button. */
export const RESET_TIMEOUT_MS = 20_000;

export type RequestResetOutcome =
  /** The server took the request. It does NOT mean an account exists — see the header. */
  | { ok: true }
  /** The request never arrived. The only failure worth a sentence. */
  | { ok: false; message: string };

export type RedeemResetOutcome =
  | { ok: true; email: string }
  | { ok: false; message: string };

interface ErrorBody {
  message?: unknown;
}

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value.trim() : null);

async function post(path: string, body: unknown, base: string, fetchImpl?: FetchLike): Promise<{ response: Response } | { failure: string }> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), RESET_TIMEOUT_MS);
  const send = fetchImpl ?? fetch;
  try {
    const response = await send(`${base}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal: abort.signal,
    });
    return { response };
  } catch (error) {
    // An abort and a dead connection read the same to a person: the app could not reach the server.
    return { failure: (error as Error)?.name === "AbortError" ? "the server did not answer in time" : "could not reach the server" };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Ask for a reset link to be sent. See the header for why this has only one success.
 */
export async function requestReset(email: string, base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<RequestResetOutcome> {
  const sent = await post(RESET_REQUEST_PATH, { email }, base, fetchImpl);
  if ("failure" in sent) return { ok: false, message: sent.failure };
  // 🔴 A non-2xx is still reported as success. The endpoint has no refusal that says
  // anything about the address, so the only thing a failure here could mean is that
  // the server is unwell — and saying "we could not send it" would tell somebody
  // probing for an address that THIS one behaved differently.
  return { ok: true };
}

/**
 * Spend a reset link and set the new password.
 *
 * 🔴 Unlike the request, this one DOES report the server's sentence — the person is
 * standing in front of a form they just filled in, and "at least 12 characters" is
 * something only they can act on. The server is careful to give one identical
 * sentence for every way a token can be no good (`set-password.ts`), so passing it
 * through leaks nothing.
 */
export async function redeemReset(
  token: string,
  newPassword: string,
  base: string = API_BASE_URL,
  fetchImpl?: FetchLike,
): Promise<RedeemResetOutcome> {
  const sent = await post(RESET_REDEEM_PATH, { token, newPassword }, base, fetchImpl);
  if ("failure" in sent) return { ok: false, message: sent.failure };

  const { response } = sent;
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ErrorBody;
    return { ok: false, message: text(body.message) ?? "this reset link is not valid any more — ask for a new one" };
  }
  const body = (await response.json().catch(() => ({}))) as { email?: unknown };
  return { ok: true, email: text(body.email) ?? "" };
}
